<template>
  <v-row id="profile-badge" align="center" justify="space-between" width="220px" class="container ma-1 mx-auto pa-1">
    <!-- DESKTOP/TABLET: show dropdown menu -->
    <template v-if="!isMobile">
      <v-menu
        v-model="menu"
        width="300"
        location="end bottom"
        offset="12"
        transition="scale-transition"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <div class="align-center cursor-pointer d-flex ga-3 w-100" v-bind="props">
            <v-btn icon v-bind="props" size="md">
              <v-avatar :color="avatarColor" size="40">
                <span class="text-caption">{{ initials }}</span>
              </v-avatar>
            </v-btn>
            <span class="font-weight-medium text-body-2">{{ authStore.user?.firstName }}</span>
          </div>
        </template>

        <v-card width="300" elevation="8" rounded="md" class="py-2">
          <!-- USER HEADER -->
          <v-sheet color="blue-lighten-5" class="align-center d-flex ga-2 mb-3 mt-2 mx-3 px-2 py-2 rounded-md" border>
            <v-avatar :color="avatarColor" size="40" class="elevation-1">
              <span class="font-weight-bold text-body-2">{{ initials }}</span>
            </v-avatar>

            <div class="flex-grow-1 user-meta">
              <div class="font-weight-semibold text-body-2">
                {{ authStore.user?.firstName }} {{ authStore.user?.lastName }}
              </div>
              <v-tooltip location="bottom" :text="authStore.user?.email">
                <template #activator="{ props }">
                  <div class="email font-italic text-caption" v-bind="props">
                    {{ authStore.user?.email }}
                  </div>
                </template>
              </v-tooltip>
            </div>
          </v-sheet>

          <!-- META INFO -->
          <v-card-text class="pb-2 pt-1 px-4">
            <div class="d-flex flex-wrap ga-2 justify-center">
              <v-chip
                size="small"
                variant="tonal"
                :color="companyColor"
                border
                prepend-icon="ri:building-line"
              >
                {{ $t("common.companies." + authStore.user?.company) }}
              </v-chip>

              <v-chip
                size="small"
                variant="tonal"
                :color="avatarColor"
                border
                prepend-icon="ri:shield-user-line"
              >
                {{ $t("users.role." + authStore.user?.role) }}
              </v-chip>
            </div>
          </v-card-text>

          <v-divider class="mx-4 my-2" />

          <v-btn
            variant="text"
            color="neutral-600"
            class="font-weight-medium h-auto py-3 text-body-2 w-100"
            prepend-icon="ri:logout-box-r-line"
            @click="handleLogout"
          >
            {{ $t("nav.logout") }}
          </v-btn>
        </v-card>
      </v-menu>
    </template>

    <!-- MOBILE: render inline -->
    <template v-else>
      <v-card class="bg-transparent container elevation-2 font-weight-bold mx-auto pa-4 text-center w-100">
        <v-avatar :color="avatarColor" size="40">
          <span class="text-caption">{{ initials }}</span>
        </v-avatar>

        <h3 class="mt-4">
          {{ `${authStore.user?.firstName} ${authStore.user?.lastName}` }}
        </h3>

        <h3 class="mt-2">
          <span class="font-weight-regular">{{ $t("auth.company") }}</span>
          {{ $t("common.companies." + authStore.user?.company) }}
        </h3>

        <p class="mt-2 text-caption">
          {{ authStore.user?.email }}
        </p>

        <v-divider class="my-3" />

        <v-btn
          variant="text"
          color="red-100"
          class="font-weight-medium h-auto py-3 text-body-2 w-100"
          prepend-icon="ri:logout-box-r-line"
          @click="authStore.logout()"
        >
          {{ $t("nav.logout") }}
        </v-btn>
      </v-card>
    </template>
  </v-row>
</template>

<script lang="ts" setup>
import { userCompany, type UserCompany } from '@/lib/api/schemas';

defineProps<{
  isMobile: boolean;
}>();

const authStore = useAuthStore();

const menu = ref(false);

const initials = computed(() => {
  const first = authStore.user?.firstName?.[0] ?? '';
  const last = authStore.user?.lastName?.[0] ?? '';
  return (first + last).toUpperCase();
});

const avatarColor = computed(() => {
  const roleColors = {
    admin: 'red-200', // strong authority
    viewer: 'blue-200', // informational / calm
    leadProvider: 'orange-200', // external / contributor
    mandatary: 'green-200', // internal / trusted partner
  };
  return roleColors[authStore.user?.role ?? 'admin'];
});

const companyColor = computed(() => {
  const companyColors: Record<UserCompany, string> = {
    capFinances: 'capfinances',
    predictis: 'predictis',
  };
  return companyColors[authStore.user?.company ?? userCompany.CapFinances];
});

const handleLogout = () => {
  menu.value = false;
  authStore.logout();
};
</script>

<style scoped lang="scss">
@media (width >=960px) {
    .container:hover {
        border-radius: 6px;
        background-color: rgb(var(--v-theme-neutral-200));
    }
}

.user-meta {
    min-width: 0;
}

.email {
    overflow: hidden;
    display: block;

    max-width: 230px;

    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
